import { EventPageSlugs } from '@apps/admin/routes/Dashboard/common/enums/EventPageSlugs';
import { EventPageType } from '@graphql/generated';

// MIGRATED_PAGES is a readonly object mapping a subset of EventPageSlugs.
const MIGRATED_PAGES = {
  faq: EventPageSlugs.Faq,
  story: EventPageSlugs.Story,
  vip: EventPageSlugs.Vip,
} as const;

/**
 * Set of page slugs that have been migrated from native pages to custom pages.
 * When the `useWeddingEventPagesAsCustomPages` feature flag is enabled, these pages
 * use direct routes (e.g., /story) instead of custom page routes (e.g., /page/story).
 */
export const MIGRATED_CUSTOM_PAGE_SLUGS: ReadonlySet<EventPageSlugs> = new Set(Object.keys(MIGRATED_PAGES) as EventPageSlugs[]);

/**
 * Set of page slugs that should always use the dictionary entry (native page config)
 * regardless of their page type. E.g., accommodations is a custom page but uses native routing.
 */
export const ALWAYS_NATIVE_PAGE_SLUGS: ReadonlySet<EventPageSlugs> = new Set([EventPageSlugs.Accommodations]);

/**
 * Check if a page slug is a migrated custom page slug.
 * @param slug - The page slug to check
 * @returns true if the slug is in MIGRATED_CUSTOM_PAGE_SLUGS
 */
export const isMigratedCustomPageSlug = (slug: string): boolean => MIGRATED_CUSTOM_PAGE_SLUGS.has(slug as EventPageSlugs);

interface PageWithTypeAndSlug {
  readonly pageSlug: string;
  readonly type?: EventPageType | null;
}

/**
 * Determines if a page should use the native/dictionary entry (with correct icon/path)
 * instead of the generic custom page handler.
 *
 * Returns true for:
 * 1. Pages in ALWAYS_NATIVE_PAGE_SLUGS (e.g., accommodations)
 * 2. Migrated custom pages when feature flag is ON (e.g., story)
 * 3. Non-custom pages (native pages)
 *
 * @param page - Page object with pageSlug and type
 * @param isFeatureEnabled - Whether the custom event pages feature is enabled
 */
export const shouldUseNativePageEntry = (page: PageWithTypeAndSlug, isFeatureEnabled: boolean): boolean => {
  // Always use native entry for specific slugs (e.g., accommodations)
  if (ALWAYS_NATIVE_PAGE_SLUGS.has(page.pageSlug as EventPageSlugs)) return true;

  // Use native entry for migrated custom pages when feature flag is ON
  if (isFeatureEnabled && isMigratedCustomPageSlug(page.pageSlug)) return true;

  // Use native entry for non-custom pages
  return page.type !== EventPageType.custom;
};

// Mapping from migrated page slugs to their corresponding legacy EventPageType
// e.g., 'story' (MIGRATED_PAGES.story) → EventPageType.story
export const SLUG_TO_LEGACY_PAGE_TYPE: Readonly<Partial<Record<EventPageSlugs, EventPageType>>> = {
  [MIGRATED_PAGES.story]: EventPageType.story,
  [MIGRATED_PAGES.faq]: EventPageType.faq,
  [MIGRATED_PAGES.vip]: EventPageType.vip,
};

// Mapping from EventPageType to canonical slug for migrated pages
// e.g., EventPageType.vip → 'vip'
const PAGE_TYPE_TO_SLUG: Readonly<Partial<Record<EventPageType, EventPageSlugs>>> = {
  [EventPageType.story]: MIGRATED_PAGES.story,
  [EventPageType.faq]: MIGRATED_PAGES.faq,
  [EventPageType.vip]: MIGRATED_PAGES.vip,
};

/**
 * Get the canonical slug for a migrated page type.
 * For VIP pages, this returns 'vip' regardless of the page's actual pageSlug ('weddingparty').
 * @param pageType - The page type to get the slug for
 * @returns The canonical slug if it's a migrated page type, undefined otherwise
 */
export const getMigratedPageSlug = (pageType: EventPageType): string | undefined => PAGE_TYPE_TO_SLUG[pageType];
